import {
  useCheckLogin,
  useCreateAdmin,
  useCreateHospitalAdmin,
  useCreateOrganizationAdmin,
  useCreateTherapist,
} from '_api';
import { AccountType, Admin, Therapist, UserPayload } from '_api/types';
import { ACCOUNT_TYPE } from '_constants';
import { DefaultNewUser } from '_constants/object-templates';
import { useMemo } from 'react';

type CreateUserCall<A, B> = (role: AccountType, body: A) => Promise<B>;

const useUser = () => {
  const { data: account } = useCheckLogin();
  const { mutateAsync: createAdmin } = useCreateAdmin();
  const { mutateAsync: createHospitalAdmin } = useCreateHospitalAdmin();
  const { mutateAsync: createOrgAdmin } = useCreateOrganizationAdmin();
  const { mutateAsync: createTherapist } = useCreateTherapist();

  const { accountType, hospitalId, id, organizationId } = useMemo(() => {
    if (account) return account as Admin;
    else {
      return {
        accountType: undefined,
        hospitalId: 0,
        id: 0,
        organizationId: 0,
      };
    }
  }, [account]);

  const canEditObject = (
    item: Partial<Record<'hospitalId' | 'organizationId' | 'userId', number>>,
  ) =>
    accountType === 'ADMIN' ||
    (accountType === 'ORGANIZATION_ADMIN' &&
      item?.organizationId &&
      item.organizationId === organizationId) ||
    (accountType === 'HOSPITAL_ADMIN' &&
      item?.hospitalId &&
      item.hospitalId === hospitalId) ||
    (accountType === 'THERAPIST' && item?.userId && item.userId === id);

  const userRoleCreateMap: Record<Exclude<AccountType, 'GUARDIAN' | 'NONE'>, any> = {
    ADMIN: createAdmin,
    ORGANIZATION_ADMIN: createOrgAdmin,
    HOSPITAL_ADMIN: createHospitalAdmin,
    THERAPIST: createTherapist,
  };

  const createUser:
    | CreateUserCall<UserPayload, Therapist>
    | CreateUserCall<UserPayload, Admin> = (userRole: AccountType, body: UserPayload) =>
    userRoleCreateMap[userRole as Exclude<AccountType, 'GUARDIAN' | 'NONE'>](body);

  const iniCreateUser = useMemo(() => {
    if (account && accountType) {
      switch (accountType) {
        case ACCOUNT_TYPE.ORG_ADMIN:
          return {
            ...DefaultNewUser,
            organizationId: organizationId,
          };
        case ACCOUNT_TYPE.HOS_ADMIN:
          return {
            ...DefaultNewUser,
            organizationId: organizationId,
            hospitalId: hospitalId,
          };
        default:
          return DefaultNewUser;
      }
    }
    return DefaultNewUser;
  }, [account]);

  const isAdmin = useMemo(
    () => accountType !== ACCOUNT_TYPE.THERAPIST && accountType !== ACCOUNT_TYPE.GUARDIAN,
    [account],
  );

  const isSuperAdmin = useMemo(() => accountType === ACCOUNT_TYPE.ADMIN, [account]);
  const isOrgAdmin = useMemo(() => accountType === ACCOUNT_TYPE.ORG_ADMIN, [account]);

  const isTherapist = useMemo(() => accountType == ACCOUNT_TYPE.THERAPIST, [account]);

  return {
    account,
    canEditObject,
    createUser,
    iniCreateUser,
    isSuperAdmin,
    isAdmin,
    isOrgAdmin,
    isTherapist,
  };
};

export default useUser;
